import {
 useState, useEffect, useCallback, useRef,
} from 'react';

import qapi from 'views_shared/app/javascript/api';
import qlog from 'views_shared/app/javascript/scripts/qlog';

const PAGE_SIZE = 20;
// A page can dedupe away entirely (every pitch already has a conversation). Keep pulling
// a bounded number of pages per call so the feed doesn't stall on a silent empty page.
const MAX_PAGES_PER_CALL = 5;

// The Everything tab's virtual rows: sent, conversationless pitches on requests the media
// user owns, newest-first with the same compound-keyset shape as useConversations. Pitches
// whose conversations exist for this receiver — INCLUDING trashed ones — are excluded here
// at fetch time (trashing a conversation must not resurrect its pitch as a virtual row).
function useVirtualPitchRows(receiverProfileId, enabled, filters = null) {
  const [pitchRows, setPitchRows] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(enabled));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  // Oldest FETCHED sent_at while more pages remain (feeds the merge frontier) — tracked
  // separately from pitchRows because a fully-deduped page keeps the cursor moving.
  const [frontier, setFrontier] = useState(null);

  const cursorRef = useRef(null);
  const fetchTokenRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  const performFetch = useCallback(
    async ({ mode }) => {
      const isAppend = mode === 'append';
      if (isAppend && isLoadingMoreRef.current) return;
      if (isAppend && !cursorRef.current) return;

      fetchTokenRef.current += 1;
      const token = fetchTokenRef.current;

      if (isAppend) {
        isLoadingMoreRef.current = true;
        setIsLoadingMore(true);
      } else {
        cursorRef.current = null;
        setIsLoading(true);
      }

      const kept = [];
      let pageHasMore = false;
      let errored = false;

      for (let page = 0; page < MAX_PAGES_PER_CALL; page += 1) {
        // Snapshot so an errored page rewinds the cursor — otherwise the next loadMore
        // resumes PAST the dropped page and its rows silently vanish from the feed.
        const pageStartCursor = cursorRef.current;
        // eslint-disable-next-line no-await-in-loop -- keyset pages are sequential by nature
        const { data, error } = await qapi.pitches.listSentOnOwnRequests(receiverProfileId, {
          cursor: cursorRef.current,
          limit: PAGE_SIZE,
          filters,
        });
        if (token !== fetchTokenRef.current) return;
        if (error) {
          qlog.error('Failed to load pitch rows:', error);
          cursorRef.current = pageStartCursor;
          errored = true;
          break;
        }

        const fetched = data || [];
        pageHasMore = fetched.length === PAGE_SIZE;
        if (fetched.length > 0) {
          const last = fetched[fetched.length - 1];
          cursorRef.current = { sentAt: last.sent_at, id: last.id };
        }

        if (fetched.length > 0) {
          // eslint-disable-next-line no-await-in-loop
          const { data: convRows, error: convError } = await qapi.conversations.getByPitchOriginIds(
            receiverProfileId,
            fetched.map((p) => p.id),
          );
          if (token !== fetchTokenRef.current) return;
          if (convError) {
            qlog.error('Failed to dedupe pitch rows against conversations:', convError);
            cursorRef.current = pageStartCursor;
            errored = true;
            break;
          }
          const originIds = new Set((convRows || []).map((c) => c.origin_id));
          kept.push(...fetched.filter((p) => !originIds.has(p.id)));
        }

        if (kept.length > 0 || !pageHasMore) break;
      }

      if (token !== fetchTokenRef.current) return;

      // A failed page must not commit "stream exhausted" — keep whatever is loaded and
      // leave frontier/rows untouched so the feed neither blanks nor lies.
      if (errored) {
        setIsLoading(false);
        setIsLoadingMore(false);
        isLoadingMoreRef.current = false;
        return;
      }

      setFrontier(pageHasMore && cursorRef.current ? cursorRef.current.sentAt : null);
      setPitchRows((prev) => {
        const base = isAppend ? prev : [];
        const seen = new Set(base.map((p) => p.id));
        return [...base, ...kept.filter((p) => !seen.has(p.id))];
      });
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
    },
    [receiverProfileId, filters],
  );

  useEffect(() => {
    if (!enabled || !receiverProfileId) {
      fetchTokenRef.current += 1; // discard any in-flight fetch
      cursorRef.current = null;
      setPitchRows([]);
      setFrontier(null);
      setIsLoading(false);
      setIsLoadingMore(false);
      isLoadingMoreRef.current = false;
      return;
    }
    performFetch({ mode: 'fresh' });
  }, [enabled, receiverProfileId, performFetch]);

  const loadMore = useCallback(() => performFetch({ mode: 'append' }), [performFetch]);
  // Deep-link inject: a resolved pitch deeper than the stream has paginated gets a row at
  // sorted position (the pitch counterpart of useConversations' preserve+inject; the merge
  // pins it through the frontier cut so it actually renders).
  const injectPitch = useCallback((pitch) => {
    setPitchRows((prev) => {
      if (prev.some((p) => p.id === pitch.id)) return prev;
      const next = [...prev, pitch];
      next.sort((a, b) => {
        if (a.sent_at !== b.sent_at) return a.sent_at < b.sent_at ? 1 : -1;
        return a.id < b.id ? 1 : -1;
      });
      return next;
    });
  }, []);

  // Optimistic read stamps — the server calls (markReadById / mark_all_read) run alongside;
  // rows are stamped in place rather than evicted so an "Unread only" list doesn't shift
  // under the user mid-read. The next refetch applies the filter strictly.
  const markPitchRead = useCallback((pitchId) => {
    const stamp = new Date().toISOString();
    setPitchRows((prev) => prev.map((p) => (p.id === pitchId && !p.read_at ? { ...p, read_at: stamp } : p)));
  }, []);

  const markAllPitchesRead = useCallback(() => {
    const stamp = new Date().toISOString();
    setPitchRows((prev) => prev.map((p) => (p.read_at ? p : { ...p, read_at: stamp })));
  }, []);

  return {
    pitchRows,
    frontier,
    isLoading,
    isLoadingMore,
    loadMore,
    injectPitch,
    markPitchRead,
    markAllPitchesRead,
  };
}

export default useVirtualPitchRows;
